import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase'; // Adjust path if needed

const SignUpManagerPage = () => {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); // Clear previous errors

        // --- Basic Validation ---
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        if (password.length < 6) {
            setError("Password must be at least 6 characters long.");
            return;
        }
        // --- End Validation ---

        setLoading(true);

        try {
            // 1. Create user in Firebase Authentication
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            console.log("Firebase Auth user created:", user.uid);

            // 2. Create user document in Firestore with 'Manager' role
            const userDocRef = doc(db, 'users', user.uid);
            await setDoc(userDocRef, {
                name: name,
                email: user.email, // Use email from Auth result for consistency
                role: 'Manager', // Explicitly set the role
                createdAt: serverTimestamp()
            });
            console.log("Firestore user document created with Manager role.");

            // 3. Redirect to login or dashboard
            // Redirecting to login is often better initially
            navigate('/login', { state: { message: "Manager account created successfully. Please log in." } });

        } catch (err) {
            console.error("Error creating manager account:", err);
            // Set user-friendly error messages
            if (err.code === 'auth/email-already-in-use') {
                setError('This email address is already registered. Try logging in.');
            } else if (err.code === 'auth/weak-password') {
                setError('The password is too weak. Please use a stronger password.');
            } else {
                setError('Failed to create manager account. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <h2>Create Initial Manager Account</h2>

            {/* --- SECURITY WARNING --- */}
            <p style={styles.warning}>
                <strong>Important:</strong> Use this page ONLY ONCE to create the first Manager account.
                After successful creation, REMOVE the route to this page in your application code (App.js)
                to prevent unauthorized Manager sign-ups.
            </p>

            {error && <p style={styles.error}>{error}</p>}

            <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.inputGroup}>
                    <label htmlFor="name">Full Name:</label>
                    <input
                        type="text"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        style={styles.input}
                    />
                </div>
                <div style={styles.inputGroup}>
                    <label htmlFor="email">Email:</label>
                    <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        style={styles.input}
                    />
                </div>
                <div style={styles.inputGroup}>
                    <label htmlFor="password">Password:</label>
                    <input
                        type="password"
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        style={styles.input}
                    />
                     <small>Minimum 6 characters</small>
                </div>
                <div style={styles.inputGroup}>
                    <label htmlFor="confirmPassword">Confirm Password:</label>
                    <input
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        style={styles.input}
                    />
                </div>
                <button type="submit" disabled={loading} style={styles.button}>
                    {loading ? 'Creating Account...' : 'Create Manager Account'}
                </button>
            </form>
            <p style={styles.loginLink}>
                Already created the manager? <Link to="/login">Login Here</Link>
            </p>
             {/* --- Add more styling as needed --- */}
        </div>
    );
};

// Basic inline styles for demonstration
const styles = {
    container: {
        maxWidth: '400px',
        margin: '40px auto',
        padding: '20px',
        border: '1px solid #ccc',
        borderRadius: '8px',
        textAlign: 'center',
        fontFamily: 'sans-serif' // Basic font
    },
     warning: {
        backgroundColor: '#fff3cd',
        color: '#856404',
        border: '1px solid #ffeeba',
        padding: '10px',
        borderRadius: '4px',
        marginBottom: '15px',
        textAlign: 'left'
    },
    error: {
        color: 'red',
        marginBottom: '15px',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px', // Spacing between input groups
    },
    inputGroup: {
        textAlign: 'left',
    },
    input: {
        width: '100%', // Make input take full width of container
        padding: '8px',
        boxSizing: 'border-box', // Include padding in width calculation
        border: '1px solid #ccc',
        borderRadius: '4px'
    },
    button: {
        padding: '10px 15px',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '16px',
    },
    loginLink: {
        marginTop: '20px',
    }
};


export default SignUpManagerPage;